import { Col, Row } from 'antd';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';

import { Comments } from 'components/comments';
import { CompareMostRecentImage } from 'components/damages/CompareMostRecentImage';
import { useDamageAttrs } from 'components/damages/withDamages';
import { getPrimaryGroupSourceObservationCreatedColumn } from 'components/DamageTable2/columns';
import { Spinner } from 'components/ui';
import { Attributes } from 'horizon/components/Damages/Attributes/Attributes';
import {
  EditAttributesForm,
  EditAttributesModal,
} from 'horizon/components/Damages/EditAttributesForm';
import { ExternalReference } from 'horizon/components/Damages/ExternalReference';
import { HorizonDamageImage } from 'horizon/components/Damages/HorizonDamageImage';
import { UpdateToPrimaryButton } from 'horizon/components/Damages/UpdateToPrimaryModal';
import {
  getObservationGroupByInspectionId,
  getPrimaryObservationForObservationGroup,
  getPrimaryObservationGroupForDamage,
} from 'horizon/components/Damages/utils';
import {
  type AtlasGqlHorizonDamage,
  type AtlasGqlHorizonDamageObservationGroup,
  useGetHorizonDamageQuery,
} from 'types/atlas-graphql';
import { EDITOR } from 'utils/access-control/roles';
import { DAMAGE_EDIT_ATTRIBUTES } from 'utils/access-control/rules';
import { useAccountContext } from 'utils/account/AccountContext';
import { isSolarInspection as isSolarInspectionType } from 'utils/constants';
import { useIsInView } from 'utils/hooks';
import { DAMAGE_EDIT_MODAL } from 'utils/release-toggles';
import { usePermissions } from 'utils/usePermissions';

import { Asset } from './Asset';
import { Controls } from './Controls';
import {
  AttributesContainer,
  ButtonContainer,
  DetailsCol,
  ExpandedRowContainer,
  ImageCol,
  SectionTitle,
  SpinnerContainer,
  StyledCol,
  StyledRow,
} from './ExpandedRow.style';

type ExpandedRowProps = {
  damage?: AtlasGqlHorizonDamage;
  damageId?: string;
  selectedSchema?: string;
  hideDetails?: boolean;
  lazyLoad?: boolean;
  taskId?: string;
  inspectionIdOfGroupToRender?: string;
  mergedDamageId?: string;
  setMergedDamageId?: (id: string | undefined) => void;
  refetchQueries?: string[];
};

export const DamageList2ExpandedRow: React.FunctionComponent<ExpandedRowProps> = ({
  damage: horizonDamage,
  damageId,
  selectedSchema,
  hideDetails,
  lazyLoad = false,
  taskId,
  inspectionIdOfGroupToRender,
  mergedDamageId,
  setMergedDamageId,
  refetchQueries,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const rowRef = useRef<HTMLDivElement>(null);
  const isInView: boolean = useIsInView(rowRef);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const wasEditingRef = useRef<boolean>(false);
  const [isRowInView, setIsRowInView] = useState<boolean>(false);
  const [commentsKey, setCommentsKey] = useState<string>(uuid());

  const { hasRole, canAccess } = usePermissions();
  const isEditor = hasRole(EDITOR);
  const isEditable = canAccess([DAMAGE_EDIT_ATTRIBUTES]) && !hideDetails;

  const { hasReleaseToggle } = useAccountContext();
  const hasDamageEditModal = hasReleaseToggle(DAMAGE_EDIT_MODAL);

  // get damage data for row if only damageId is provided
  const { data: damageData, loading: loadingDamage } = useGetHorizonDamageQuery({
    variables: { input: { id: damageId } },
    skip: !!horizonDamage || !isRowInView,
  });

  const damage =
    horizonDamage ?? (damageData?.getHorizonDamage as AtlasGqlHorizonDamage | null | undefined);
  const { asset, primaryObservationGroupAttrs: attrs = {} } = damage ?? {};

  const { damageColumns: _damageColumns, loading: loadingDamageAttrs } = useDamageAttrs(
    selectedSchema,
    true
  );
  const damageColumns = useMemo(
    // These columns only render Attribute values here, so there is no date range
    // selection to limit.
    () => [..._damageColumns, getPrimaryGroupSourceObservationCreatedColumn(false)],
    [_damageColumns]
  );

  const primaryGroup = getPrimaryObservationGroupForDamage(
    damage
  ) as AtlasGqlHorizonDamageObservationGroup;
  const targetGroup = inspectionIdOfGroupToRender
    ? getObservationGroupByInspectionId(damage, inspectionIdOfGroupToRender)
    : primaryGroup;
  const areGroupsDifferent = primaryGroup && targetGroup && primaryGroup.id !== targetGroup.id;

  const attrsToRender = {
    ...((inspectionIdOfGroupToRender ? targetGroup?.groupAttrs : primaryGroup?.groupAttrs) ??
      attrs),
    primaryGroupSourceObservationCreated: damage?.primaryGroupSourceObservationCreated,
  };
  const criticalToRender = inspectionIdOfGroupToRender
    ? targetGroup?.groupCritical
    : primaryGroup?.groupCritical;
  const groupInspectionTypeName = targetGroup?.observations?.find(
    (observation) => !!observation?.inspection?.type?.name
  )?.inspection?.type?.name;
  const inspectionTypeName = groupInspectionTypeName;
  const isSolarInspection = isSolarInspectionType(inspectionTypeName);
  const groupInspectionId = targetGroup?.inspectionId;

  const expandToInspectionId = isSolarInspection
    ? (inspectionIdOfGroupToRender ?? groupInspectionId)
    : undefined;

  const pictureForComparison = targetGroup?.observations?.find(
    (o) => o?.id === targetGroup.sourceObservationId
  )?.picture;

  const filteredDamageColumns = damageColumns.filter((column) => {
    if (column.key === 'Severity') {
      // @ts-ignore
      return column.key in attrsToRender || targetGroup?.groupCritical;
    }
    return column.key in attrsToRender;
  });

  // update component when its damage has been merged
  useEffect(() => {
    if (mergedDamageId && setMergedDamageId) {
      if (mergedDamageId === damage?.id) {
        setCommentsKey(uuid());
        setMergedDamageId(undefined);
      }
    }
  }, [mergedDamageId, setMergedDamageId, damage?.id, setCommentsKey]);

  // render component once its in view for the first time
  useEffect(() => {
    if ((!lazyLoad || isInView) && !isRowInView) {
      setIsRowInView(true);
    }
  }, [lazyLoad, isInView, isRowInView]);

  // The inline form is taller than the read-only view, so closing it can leave the row above the
  // viewport. Measure after it collapses and scroll only as far as needed; the modal never changes
  // the row's layout, so it has nothing to restore.
  useLayoutEffect(() => {
    if (wasEditingRef.current && !isEditing && !hasDamageEditModal) {
      rowRef.current?.scrollIntoView({ block: 'nearest' });
    }
    wasEditingRef.current = isEditing;
  }, [isEditing, hasDamageEditModal]);

  const handleCloseEdit = () => setIsEditing(false);

  return (
    <ExpandedRowContainer gutter={16} ref={rowRef} data-testid="damage-expanded-row">
      {(!damage || loadingDamage || loadingDamageAttrs) && (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      )}
      {isRowInView && damage && !loadingDamage && !loadingDamageAttrs && (
        <>
          <ImageCol>
            <HorizonDamageImage
              damageId={damage.id}
              damage={damage}
              inspectionIdOfGroupToRender={inspectionIdOfGroupToRender}
              expandToInspectionId={expandToInspectionId}
              shouldShowExpand={true}
              fetchCompareDamagesData={true}
            />
          </ImageCol>
          <DetailsCol>
            <StyledRow gutter={16}>
              <StyledCol span={hideDetails ? 24 : 16}>
                <StyledRow gutter={16}>
                  <Col
                    span={12}
                    style={
                      isEditing && !hasDamageEditModal
                        ? { maxHeight: '500px', display: 'flex', flexDirection: 'column' }
                        : {}
                    }
                  >
                    <SectionTitle>{t('damage.damageAttributes', 'Damage Attributes')}</SectionTitle>
                    {isEditable && isEditing && !hasDamageEditModal ? (
                      <EditAttributesForm
                        onCancel={handleCloseEdit}
                        damageId={damage.id}
                        selectedSchema={selectedSchema}
                        observationGroup={primaryGroup}
                        backgroundColor={theme.tertiaryBackgroundColor}
                      />
                    ) : (
                      <AttributesContainer>
                        <div>
                          <Attributes
                            attrs={attrsToRender}
                            critical={criticalToRender}
                            damageColumns={filteredDamageColumns}
                            layout={{ number: 2, threshold: 7 }}
                          />
                          <ExternalReference
                            observation={getPrimaryObservationForObservationGroup(targetGroup)}
                          />
                        </div>
                        {!!taskId && (
                          <ButtonContainer>
                            {!!pictureForComparison && !!targetGroup?.sourceObservationId && (
                              <CompareMostRecentImage
                                picture={pictureForComparison}
                                observationId={targetGroup.sourceObservationId}
                              />
                            )}
                            {isEditor && areGroupsDifferent && (
                              <UpdateToPrimaryButton taskId={taskId} />
                            )}
                          </ButtonContainer>
                        )}
                      </AttributesContainer>
                    )}
                    {isEditable && hasDamageEditModal && (
                      <EditAttributesModal
                        open={isEditing}
                        onCancel={handleCloseEdit}
                        damageId={damage.id}
                        selectedSchema={selectedSchema}
                        observationGroup={primaryGroup}
                      />
                    )}
                  </Col>
                  <Col span={12}>
                    <SectionTitle>{t('table.asset', 'Asset')}</SectionTitle>
                    {asset && <Asset asset={asset} />}
                  </Col>
                </StyledRow>
                {!hideDetails && (
                  <Row>
                    <Col span={24}>
                      <Controls
                        onClickEdit={() => setIsEditing(true)}
                        damage={damage}
                        refetchQueries={refetchQueries}
                      />
                    </Col>
                  </Row>
                )}
              </StyledCol>
              {!hideDetails && (
                <Col span={8}>
                  <SectionTitle>{t('comments.title', 'Comments')}</SectionTitle>
                  <Comments
                    key={commentsKey}
                    threadId={primaryGroup.id}
                    loading={false}
                    maxHeight="10rem"
                  />
                </Col>
              )}
            </StyledRow>
          </DetailsCol>
        </>
      )}
    </ExpandedRowContainer>
  );
};
